import { contentful } from '@troon/contentful';
import { getRequestEvent } from 'solid-js/web';
import { captureException } from '@sentry/solidstart';
import { Error404 } from '../../../../graphql';
import { getLogger } from '../../../../middleware/logging';
import { defaultTTL, modelTTLOverride, ttlCache } from './cache';
import type { ContentfulModel } from '@troon/contentful';

export class NotFoundError extends Error {}

export async function getEntryServer<
	M extends ContentfulModel,
	R extends ReturnType<(typeof contentful)[M]['get']> = ReturnType<(typeof contentful)[M]['get']>,
	O extends Parameters<(typeof contentful)[M]['get']>[1] = Parameters<(typeof contentful)[M]['get']>[1],
>(model: M, id: string, opts: O): Promise<R | undefined> {
	'use server';

	const url = getRequestEvent()?.request.url ? new URL(getRequestEvent()!.request.url) : new URL('');
	const useCache = !url.searchParams.has('preview');

	const logger = getLogger();
	const key = `${model}-${id}`;
	const cached = ttlCache.get(key);
	if (useCache && cached) {
		logger.debug({ key, useCache }, 'Returning cached contentful entry');
		return cached as Awaited<R>;
	}

	logger.debug({ key, useCache }, 'No cache for contentful entry');

	try {
		const res = await contentful[model].get(id, opts);

		if (!res && res !== null) {
			logger.warn({ key }, 'Content not found');
			throw new NotFoundError();
		}

		if (useCache) {
			ttlCache.set(key, res, { ttl: modelTTLOverride[model] ?? defaultTTL });
		}
		return res as Awaited<R>;
	} catch (e) {
		if (e instanceof NotFoundError) {
			throw e;
		}
		logger.error(e);
		captureException(e);
		throw new Error404('Unable to load content');
	}
}

export async function getEntriesServer<
	M extends ContentfulModel,
	R extends ReturnType<(typeof contentful)[M]['list']> = ReturnType<(typeof contentful)[M]['list']>,
	O extends Parameters<(typeof contentful)[M]['list']>[0] = Parameters<(typeof contentful)[M]['list']>[0],
>(model: M, opts: O): Promise<R> {
	'use server';
	const url = getRequestEvent()?.request.url ? new URL(getRequestEvent()!.request.url) : new URL('');
	const useCache = !url.searchParams.has('preview');

	const logger = getLogger();
	logger.debug({ url: url.toString() }, 'Getting contentful entries');
	const key = `$${model}-${JSON.stringify(opts)}`;

	const cached = ttlCache.get(key);
	if (useCache && cached) {
		logger.debug({ key, useCache }, 'Returning cached contentful list');
		return cached as Awaited<R>;
	}

	logger.debug({ key, useCache }, 'No cache for contentful list');

	try {
		const res = await contentful[model].list(opts);

		if (!res && res !== null) {
			logger.warn({ key }, 'Content not found');
		}

		if (useCache) {
			ttlCache.set(key, res, { ttl: modelTTLOverride[model] ?? defaultTTL });
		}
		return res as Awaited<R>;
	} catch (e) {
		logger.error(e);
		captureException(e);
		throw new Error404('Unable to load content');
	}
}
